// src/translations/translations.js
import dayjs from 'dayjs'
import { logger } from '@helpers/logger.js'

// Define all supported locales
export const supportedLocales = ['en-za', 'en', 'af', 'zu'] // Add more locales as needed

// Create a mapping for translation files
const translationModules = {
  en: () => import('./en.json5'),
  'en-za': () => import('./en.json5'),
  af: () => import('./af.json5'),
  zu: () => import('./zu.json5'),
  // Add more mappings as needed
}

// Create a mapping for dayjs locales
const dayjsLocaleModules = {
  en: () => import('dayjs/locale/en'),
  'en-za': () => import('dayjs/locale/en'),
  af: () => import('dayjs/locale/af'),
  zu: () => import('dayjs/locale/en'),
  // Add more mappings as needed
}

// Initialize a cache to store loaded translations
const translationCache = {}

// Initialize a cache to store in-flight loading promises
const inFlightPromises = {}

// Initialize variables to hold current translations and locale
let translations = {}
let currentLocale = 'en'

// Function to asynchronously set the active locale by loading translation files and dayjs locales
export const setActiveLocale = async (locale = 'en') => {
  if (!supportedLocales.includes(locale)) {
    console.warn(`Locale "${locale}" is not supported. Falling back to "en".`)
    locale = 'en'
  }

  // If translations for the locale are already cached, use them
  if (translationCache[locale]) {
    translations = translationCache[locale]
    currentLocale = locale

    // Set the locale for dayjs
    try {
      await dayjsLocaleModules[locale]()
      dayjs.locale(locale)
    } catch (dayjsError) {
      console.error(`Error loading dayjs locale "${locale}":`, dayjsError)
      dayjs.locale('en')
    }

    return translations // Return loaded translations
  }

  // If there's already a loading process for this locale, wait for it
  if (inFlightPromises[locale]) {
    await inFlightPromises[locale]
    translations = translationCache[locale]
    currentLocale = locale

    // Set the locale for dayjs
    try {
      await dayjsLocaleModules[locale]()
      dayjs.locale(locale)
    } catch (dayjsError) {
      console.error(`Error loading dayjs locale "${locale}":`, dayjsError)
      dayjs.locale('en')
    }

    return translations // Return loaded translations
  }

  // Create a loading promise and store it in the in-flight cache
  const loadingPromise = (async () => {
    try {
      // Dynamically import the translation file using the mapping
      const module = await translationModules[locale]()
      translationCache[locale] = module.default
      translations = module.default
      currentLocale = locale

      // Dynamically import the corresponding dayjs locale using the mapping
      await dayjsLocaleModules[locale]()
      dayjs.locale(locale)
    } catch (error) {
      console.error(`Error loading translations or locale for "${locale}":`, error)
      // Optionally, fallback to a default locale if desired
      if (locale !== 'en') {
        return await setActiveLocale('en')
      }
    } finally {
      // Remove the promise from in-flight cache once done
      delete inFlightPromises[locale]
    }
    return translations // Return loaded translations
  })()

  inFlightPromises[locale] = loadingPromise
  return await loadingPromise
}

// Function to format dates using dayjs
export const getLocalShortDateString = (date) => {
  return dayjs(date).format('D MMM YYYY')
}

// Translator function to retrieve translated strings
export const translator = (key) => {
  return translations[key] || key
}

// Async function to preload all supported locales
export const preloadAllLocales = async () => {
  const startTime = performance.now() // Capture the start time

  try {
    // Create an array of promises for loading each locale
    const preloadPromises = supportedLocales.map(async (locale) => {
      if (translationCache[locale]) {
        // Already loaded, skip
        return
      }

      if (inFlightPromises[locale]) {
        // Already being loaded, wait for it
        return inFlightPromises[locale]
      }

      // Start loading the locale using the mapping
      const loadingPromise = (async () => {
        try {
          // Dynamically import the translation file using the mapping
          const translationModule = await translationModules[locale]()
          translationCache[locale] = translationModule.default

          // Dynamically import the dayjs locale using the mapping
          await dayjsLocaleModules[locale]()
        } catch (error) {
          logger.error(`Error preloading locale "${locale}":`, error)
        } finally {
          // Remove from in-flight cache
          delete inFlightPromises[locale]
        }
      })()

      inFlightPromises[locale] = loadingPromise
      return loadingPromise
    })

    // Wait for all locales to be preloaded
    await Promise.all(preloadPromises)
    const endTime = performance.now() // Capture the end time
    const duration = endTime - startTime // Calculate duration in milliseconds

    logger.log(`All locales preloaded successfully in ${duration.toFixed(2)} ms.`)
  } catch (error) {
    logger.error('Error preloading locales:', error)
  }
}

// Export currentLocale for external use if needed
export const getCurrentLocale = () => currentLocale
